import React, { useEffect, useState } from 'react'
import { Empty, Spin } from 'antd'
import { useHistory } from 'react-router-dom'
import { getAicDashboardExceptions, getAicDashboardSummary } from '../../request'
import MyProjectsList from './MyProjectsList'
import MySpaceCard from './MySpaceCard'
import MyVibeProjectCreateDialog from './MyVibeProjectCreateDialog'
import { EMPTY, formatDateTime, formatInteger, formatRate, termDisplay, unwrapRows } from './terminalModel'

const EXCEPTIONS_PAGE = '/portal/aic-dashboard/v2/exceptions'
const DATASETS_PAGE = '/portal/aic-dashboard/v2/datasets'

const settle = promise => promise.then(value => ({ ok: true, value }), () => ({ ok: false, value: null }))

const buildKpis = summary => {
  const source = summary || {}
  return [
    { key: 'units', label: '数据单元', value: formatInteger(source.datasetCount), note: '当前账号可见的 AIC 数据源' },
    { key: 'healthy', label: '正常', value: formatInteger(source.healthyCount), note: '按导入计划或更新周期按时上传' },
    { key: 'warning', label: '未更新', value: formatInteger(source.warningCount), note: '已超过一个更新周期未成功上传' },
    { key: 'unknown', label: '未配置周期', value: formatInteger(source.unknownCount), note: '没有可判定的更新周期，无法判断断更' },
    { key: 'rate', label: '健康率', value: formatRate(source.healthyRate), note: '正常 / 可计算的数据单元' },
    { key: 'rules', label: '未配置规则', value: formatInteger(source.unconfiguredRuleDatasetCount), note: '导入计划、数据日期、质量规则均未启用' }
  ]
}

/** 总览：KPI 磁贴 + 我的空间 / 我的项目 + 待关注异常 Top5 + 口径说明，全部来自真实只读接口。 */
export default function TerminalOverview () {
  const history = useHistory()
  const [summary, setSummary] = useState(null)
  const [exceptions, setExceptions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  // 新建项目弹窗与项目列表刷新令牌：新建成功后只重载「我的项目」列表，不整页重载。
  const [createVisible, setCreateVisible] = useState(false)
  const [projectsReloadToken, setProjectsReloadToken] = useState(0)

  useEffect(() => {
    let mounted = true
    setLoading(true)
    setError('')
    Promise.all([
      settle(getAicDashboardSummary({})),
      settle(getAicDashboardExceptions({}))
    ]).then(([summaryResult, exceptionResult]) => {
      if (!mounted) return
      if (summaryResult.ok) setSummary(summaryResult.value || {})
      if (exceptionResult.ok) setExceptions(unwrapRows(exceptionResult.value))
      const failed = [
        !summaryResult.ok && '总览统计',
        !exceptionResult.ok && '异常列表'
      ].filter(Boolean)
      setError(failed.length > 0 ? `${failed.join('、')}加载失败，其余内容仍可使用` : '')
    }).finally(() => {
      if (mounted) setLoading(false)
    })
    return () => { mounted = false }
  }, [])

  const attention = exceptions.slice(0, 5)

  const openCreateDialog = () => setCreateVisible(true)
  const closeCreateDialog = () => setCreateVisible(false)
  const handleProjectCreated = () => {
    setCreateVisible(false)
    // 新建是接入长链路：这里只刷新「我的项目」列表，不轮询状态。
    setProjectsReloadToken(token => token + 1)
  }

  return <div className="term-page">
    {error && <div className="term-notice term-notice-error">{error}</div>}
    <div className="term-page-head">
      <div className="term-page-title">数据可靠性总览</div>
      <div className="term-page-sub">AIC 控制面实时快照 · 不包含业务数据行内容</div>
    </div>
    <Spin spinning={loading}>
      <div className="term-kpis">
        {buildKpis(summary).map(item => <div key={item.key} className={`term-kpi term-kpi-${item.key}`}>
          <div className="term-kpi-label">{item.label}</div>
          <div className="term-kpi-value">{item.value}</div>
          <div className="term-kpi-note">{item.note}</div>
        </div>)}
      </div>
      {/* P4：空间级能力与我的项目；两个分区各自加载，失败互不影响。 */}
      <MySpaceCard onOpenCreate={openCreateDialog} />
      <MyProjectsList reloadToken={projectsReloadToken} />
      <div className="term-panel">
        <div className="term-panel-title">
          待关注异常（Top 5）
          <button type="button" className="term-link term-panel-link" onClick={() => history.push(EXCEPTIONS_PAGE)}>查看异常中心</button>
        </div>
        {attention.length === 0
          ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="当前没有待关注异常" />
          : <table className="term-table term-table-plain">
            <thead>
              <tr>
                <th>问题</th>
                <th>类型</th>
                <th>严重程度</th>
                <th>状态</th>
                <th>负责人</th>
                <th>最近发现</th>
              </tr>
            </thead>
            <tbody>
              {attention.map(item => <tr key={item.id || item.projectId} className="term-row">
                <td className="term-td term-ellipsis" title={termDisplay(item.title)}>{termDisplay(item.title)}</td>
                <td className="term-td term-nowrap">{termDisplay(item.type)}</td>
                <td className="term-td term-nowrap">{termDisplay(item.severity)}</td>
                <td className="term-td term-nowrap">{termDisplay(item.status)}</td>
                <td className="term-td term-nowrap">{termDisplay(item.owner)}</td>
                <td className="term-td term-nowrap">{item.lastSeenAt ? formatDateTime(item.lastSeenAt, EMPTY) : EMPTY}</td>
              </tr>)}
            </tbody>
          </table>}
      </div>
    </Spin>
    <div className="term-foot">
      健康判定只看上传及时性：有导入计划按计划截止时间判，没有计划则按“上次成功上传 + 一个更新周期”判；与数据质量、数据日期无关。
      <button type="button" className="term-link term-foot-link" onClick={() => history.push(DATASETS_PAGE)}>查看数据单元总账</button>
    </div>
    <MyVibeProjectCreateDialog
      visible={createVisible}
      onClose={closeCreateDialog}
      onCreated={handleProjectCreated}
    />
  </div>
}
